import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { joinChallenge, createChallenge, joinPublicChallenge } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { OfflinePage } from '../components/OfflineComponents';
import { safeSetItem } from '../utils/storageUtils';
import { useMyChallenges, usePublicChallenges, useInvalidateChallenges, qk } from '../lib/queries';
import { 
  ChevronRight, Plus, Users, Calendar, Copy, Check,
  Trophy, Target, Flame, Globe, Lock, Loader2, SlidersHorizontal, Search, X
} from 'lucide-react';

// Get cached challenges data synchronously
const getCachedChallenges = () => {
  try {
    const cached = localStorage.getItem('challenges_cache');
    if (cached) {
      const { mine, public: pub, timestamp } = JSON.parse(cached);
      // Cache valid for 2 minutes
      if (mine && Date.now() - timestamp < 2 * 60 * 1000) {
        return { mine, public: pub };
      }
    }
  } catch (e) {}
  return null;
};

export default function Challenges() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const queryClient = useQueryClient();
  
  // Initialize from cache IMMEDIATELY for instant render (before React Query fetches).
  const cachedData = getCachedChallenges();

  // Shared React Query hooks — data cached across navigations, auto-dedup'd.
  const { data: myChallengesData, isLoading: loadingMine, isError: errorMine, refetch: refetchMine } = useMyChallenges();
  const { data: publicChallengesData, isLoading: loadingPublic, refetch: refetchPublic } = usePublicChallenges();
  const invalidateChallenges = useInvalidateChallenges();

  // Prefer fresh data, fall back to cached bootstrap data while first fetch is pending.
  const challenges = myChallengesData ?? cachedData?.mine ?? [];
  const publicChallenges = publicChallengesData ?? cachedData?.public ?? [];
  const loading = (loadingMine || loadingPublic) && !cachedData;
  const networkError = errorMine && !myChallengesData;

  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('mine'); // 'mine' or 'public'

  // Public challenges filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterLang, setFilterLang] = useState('all');          // 'all' | 'it' | 'en' | 'es' | 'pt' | 'de' | 'fr'
  const [filterParticipants, setFilterParticipants] = useState('all'); // 'all' | 'small' | 'medium' | 'large'
  const [filterDuration, setFilterDuration] = useState('all');  // 'all' | 'ending' | 'month' | 'long'
  const [filterStatus, setFilterStatus] = useState('all');      // 'all' | 'upcoming' | 'justStarted'
  const [sortBy, setSortBy] = useState('new');                  // 'new' | 'popular' | 'ending'

  // Keep the localStorage mirror fresh for instant-load-on-cold-start.
  useEffect(() => {
    if (myChallengesData || publicChallengesData) {
      safeSetItem('challenges_cache', {
        mine: myChallengesData || [],
        public: publicChallengesData || [],
        timestamp: Date.now(),
      });
    }
  }, [myChallengesData, publicChallengesData]);

  // Reload helper used by pull-to-refresh and after mutations.
  const loadChallenges = useCallback(async () => {
    // Invalidate + refetch so React Query consumers (and future Dashboard) stay fresh.
    invalidateChallenges();
    await Promise.all([refetchMine(), refetchPublic()]);
  }, [invalidateChallenges, refetchMine, refetchPublic]);

  // Pull to refresh - shows loader because user explicitly requested

  // Search filter applied to all challenges (mine + public). Matches
  // against challenge name and description case-insensitively.
  const matchesSearch = (c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (c.name || '').toLowerCase().includes(q) ||
           (c.description || '').toLowerCase().includes(q);
  };

  const activeChallenges = challenges.filter(c =>
    new Date(c.end_date) >= new Date() && matchesSearch(c)
  );
  
  const pastChallenges = challenges.filter(c =>
    new Date(c.end_date) < new Date() && matchesSearch(c)
  );

  // Apply filters + sort on public challenges
  const nowMs = Date.now();
  const daysLeftOf = (c) => Math.ceil((new Date(c.end_date) - new Date()) / (1000 * 60 * 60 * 24));
  const isUpcoming = (c) => new Date(c.start_date) > new Date();
  const startedDaysAgo = (c) => Math.floor((new Date() - new Date(c.start_date)) / (1000 * 60 * 60 * 24));
  const createdDaysAgo = (c) => Math.floor((nowMs - new Date(c.created_at).getTime()) / (1000 * 60 * 60 * 24));

  const filteredPublic = publicChallenges
    .filter(c => {
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const hit = (c.name || '').toLowerCase().includes(q) ||
                    (c.description || '').toLowerCase().includes(q) ||
                    (c.creator_name || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (filterLang !== 'all' && (c.creator_language || 'it') !== filterLang) return false;
      const n = c.participant_count ?? (c.participants?.length || 0);
      if (filterParticipants === 'small' && n >= 10) return false;
      if (filterParticipants === 'medium' && (n < 10 || n > 50)) return false;
      if (filterParticipants === 'large' && n <= 50) return false;
      const d = daysLeftOf(c);
      if (filterDuration === 'ending' && d > 7) return false;
      if (filterDuration === 'month' && (d <= 7 || d > 30)) return false;
      if (filterDuration === 'long' && d <= 30) return false;
      if (filterStatus === 'upcoming' && !isUpcoming(c)) return false;
      if (filterStatus === 'justStarted' && (isUpcoming(c) || startedDaysAgo(c) > 7)) return false;
      if (filterStatus === 'new' && createdDaysAgo(c) > 7) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'popular') {
        return (b.participant_count ?? b.participants?.length ?? 0) - (a.participant_count ?? a.participants?.length ?? 0);
      }
      if (sortBy === 'ending') {
        return daysLeftOf(a) - daysLeftOf(b);
      }
      // default: new (most recently created first)
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const activeFilterCount =
    (filterLang !== 'all' ? 1 : 0) +
    (filterParticipants !== 'all' ? 1 : 0) +
    (filterDuration !== 'all' ? 1 : 0) +
    (filterStatus !== 'all' ? 1 : 0) +
    (sortBy !== 'new' ? 1 : 0);

  const resetFilters = () => {
    setFilterLang('all');
    setFilterParticipants('all');
    setFilterDuration('all');
    setFilterStatus('all');
    setSortBy('new');
  };

  if (loading) {
    return (
      <div className="page-enter px-4 pt-6">
        <h1 className={`font-display text-2xl mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Challenge</h1>
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="glass-card p-4">
              <div className="h-6 w-32 skeleton mb-3" />
              <div className="h-4 w-48 skeleton mb-3" />
              <div className="h-8 skeleton" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show offline page if no data and offline
  if ((!isOnline || networkError) && challenges.length === 0) {
    return <OfflinePage onRetry={loadChallenges} />;
  }

  return (
    <div className={`min-h-full ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="challenges-page">
      
      {/* Fixed Header */}
      <div 
        className={`fixed top-0 left-0 right-0 z-30 px-4 pb-3 ${isDark ? 'bg-[#080D1A]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <div className="max-w-md mx-auto w-full">
          <div className="flex items-center justify-between mb-4">
            <h1 className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('challenges.title')}</h1>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowJoinModal(true)}
                className="btn-secondary text-sm px-4 py-2"
                data-testid="join-challenge-btn"
              >
                {t('challenges.inviteCode')}
              </button>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="btn-primary text-sm px-4 py-2"
                data-testid="create-challenge-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('mine')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeTab === 'mine'
                  ? 'bg-[#E8334A] text-white'
                  : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Lock className="w-4 h-4" />
              {t('challenges.tabs.mine')}
            </button>
            <button
              onClick={() => setActiveTab('public')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeTab === 'public'
                  ? 'bg-[#3B82F6] text-white'
                  : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Globe className="w-4 h-4" />
              {t('challenges.public')}
              {publicChallenges.length > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'public' ? 'bg-white/20' : 'bg-[#3B82F6] text-white'
                }`}>
                  {publicChallenges.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div style={{ height: 'calc(env(safe-area-inset-top, 0px) + 130px)' }} />

      {/* Content */}
      <div className="pb-24">

        {/* Global search bar — applies to BOTH "Le mie" and "Pubblica"
            tabs. Filters by challenge name and description as the user
            types (instant client-side filter; data is already fetched
            via React Query for both tabs). */}
        <div className="px-4 pt-4 pb-4">
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ${
            isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-100'
          }`}>
            <Search className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('challenges.searchPlaceholder', 'Cerca per nome o descrizione...')}
              className={`flex-1 bg-transparent outline-none text-sm ${isDark ? 'text-white placeholder:text-white/40' : 'text-gray-900 placeholder:text-gray-400'}`}
              data-testid="challenges-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                data-testid="challenges-search-clear"
                className={`p-1 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
              >
                <X className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
              </button>
            )}
          </div>
        </div>

        {activeTab === 'mine' ? (
          // My Challenges Tab
          <>
            {challenges.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  isDark ? 'bg-[#1B3A6B]/50' : 'bg-[#1B3A6B]/10'
                }`}>
                  <Trophy className={`w-8 h-8 ${isDark ? 'text-white/40' : 'text-[#1B3A6B]'}`} />
                </div>
                <h3 className={`font-display text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('challenges.noChallenges')}
                </h3>
                <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('challenges.createFirst')}
                </p>
                <div className="flex gap-3 justify-center">
                  <button 
                    onClick={() => setShowCreateModal(true)}
                    className="btn-primary"
                  >
                    {t('challenges.create')}
                  </button>
                  <button 
                    onClick={() => setActiveTab('public')}
                    className="btn-secondary"
                  >
                    {t('challenges.explore')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Active Challenges */}
                {activeChallenges.length > 0 && (
                  <div className="px-4 mb-6">
                    <h2 className={`text-sm font-medium mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      {t('challenges.tabs.active').toUpperCase()}
                    </h2>
                    <div className="space-y-3">
                      {activeChallenges.map(challenge => (
                        <ChallengeCard 
                          key={challenge.challenge_id}
                          challenge={challenge}
                          isDark={isDark}
                          t={t}
                          onClick={() => navigate(`/app/challenge/${challenge.challenge_id}`)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Past Challenges */}
                {pastChallenges.length > 0 && (
                  <div className="px-4">
                    <h2 className={`text-sm font-medium mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      COMPLETATE
                    </h2>
                    <div className="space-y-3">
                      {pastChallenges.map(challenge => (
                        <ChallengeCard 
                          key={challenge.challenge_id}
                          challenge={challenge}
                          isDark={isDark}
                          t={t}
                          onClick={() => navigate(`/app/challenge/${challenge.challenge_id}`)}
                          past
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          // Public Challenges Tab
          <>
            {publicChallenges.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  isDark ? 'bg-[#3B82F6]/20' : 'bg-[#3B82F6]/10'
                }`}>
                  <Globe className={`w-8 h-8 ${isDark ? 'text-[#3B82F6]' : 'text-[#3B82F6]'}`} />
                </div>
                <h3 className={`font-display text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('challenges.noPublicChallenges')}
                </h3>
                <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('challenges.beFirstToCreate')}
                </p>
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  {t('challenges.createPublic')}
                </button>
              </div>
            ) : (
              <div className="px-4 pt-6">
                <p className={`text-sm mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {t('challenges.joinCommunityChallenge')}
                </p>

                {/* Filter button only — search is in the global bar above */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1" />
                  <button
                    onClick={() => setShowFilters(true)}
                    className={`relative w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      activeFilterCount > 0
                        ? 'bg-[#E8334A] text-white'
                        : isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-700'
                    }`}
                    data-testid="public-challenges-filter-btn"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    {activeFilterCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white text-[#E8334A] text-[10px] font-bold flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>

                {filteredPublic.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className={`text-sm mb-3 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      Nessuna challenge corrisponde ai filtri
                    </p>
                    <button
                      onClick={() => { resetFilters(); setSearchQuery(''); }}
                      className={`text-sm font-medium ${isDark ? 'text-[#E8334A]' : 'text-[#E8334A]'}`}
                    >
                      Azzera filtri
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredPublic.map(challenge => (
                      <PublicChallengeCard 
                        key={challenge.challenge_id}
                        challenge={challenge}
                        isDark={isDark}
                        t={t}
                        onClick={() => navigate(`/app/challenge/${challenge.challenge_id}`)}
                        onJoin={async () => {
                          // Optimistic removal: drop this challenge from the public list
                          // cache immediately so it disappears from the UI before the
                          // backend round-trip completes (prevents double-clicks).
                          const prevPublic = queryClient.getQueryData(qk.publicChallenges);
                          queryClient.setQueryData(qk.publicChallenges, (old) =>
                            (old || []).filter(c => c.challenge_id !== challenge.challenge_id)
                          );
                          try {
                            await joinPublicChallenge(challenge.challenge_id);
                            // Refresh both lists (new challenge appears under "My")
                            loadChallenges();
                          } catch (err) {
                            console.error('Join error:', err);
                            // Rollback on failure
                            if (prevPublic) queryClient.setQueryData(qk.publicChallenges, prevPublic);
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Public Filters Modal */}
      {showFilters && (
        <div 
          className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm z-[60] p-0 sm:p-4"
          onClick={() => setShowFilters(false)}
        >
          <div 
            className={`w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[85vh] overflow-y-auto ${
              isDark ? 'bg-[#0D1424]' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
            style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))' }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Filtri & Ordinamento
              </h3>
              <button 
                onClick={() => setShowFilters(false)}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-gray-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <FilterSection label="Ordina per" isDark={isDark}>
              <FilterChip active={sortBy === 'new'} onClick={() => setSortBy('new')} isDark={isDark}>Nuove</FilterChip>
              <FilterChip active={sortBy === 'popular'} onClick={() => setSortBy('popular')} isDark={isDark}>Più popolari</FilterChip>
              <FilterChip active={sortBy === 'ending'} onClick={() => setSortBy('ending')} isDark={isDark}>In scadenza</FilterChip>
            </FilterSection>

            <FilterSection label="Lingua" isDark={isDark}>
              <FilterChip active={filterLang === 'all'} onClick={() => setFilterLang('all')} isDark={isDark}>Tutte</FilterChip>
              <FilterChip active={filterLang === 'it'} onClick={() => setFilterLang('it')} isDark={isDark}>🇮🇹 Italiano</FilterChip>
              <FilterChip active={filterLang === 'en'} onClick={() => setFilterLang('en')} isDark={isDark}>🇬🇧 English</FilterChip>
              <FilterChip active={filterLang === 'es'} onClick={() => setFilterLang('es')} isDark={isDark}>🇪🇸 Español</FilterChip>
              <FilterChip active={filterLang === 'pt'} onClick={() => setFilterLang('pt')} isDark={isDark}>🇵🇹 Português</FilterChip>
              <FilterChip active={filterLang === 'de'} onClick={() => setFilterLang('de')} isDark={isDark}>🇩🇪 Deutsch</FilterChip>
              <FilterChip active={filterLang === 'fr'} onClick={() => setFilterLang('fr')} isDark={isDark}>🇫🇷 Français</FilterChip>
            </FilterSection>

            <FilterSection label="Numero partecipanti" isDark={isDark}>
              <FilterChip active={filterParticipants === 'all'} onClick={() => setFilterParticipants('all')} isDark={isDark}>Tutti</FilterChip>
              <FilterChip active={filterParticipants === 'small'} onClick={() => setFilterParticipants('small')} isDark={isDark}>&lt; 10</FilterChip>
              <FilterChip active={filterParticipants === 'medium'} onClick={() => setFilterParticipants('medium')} isDark={isDark}>10–50</FilterChip>
              <FilterChip active={filterParticipants === 'large'} onClick={() => setFilterParticipants('large')} isDark={isDark}>50+</FilterChip>
            </FilterSection>

            <FilterSection label="Durata rimanente" isDark={isDark}>
              <FilterChip active={filterDuration === 'all'} onClick={() => setFilterDuration('all')} isDark={isDark}>Tutte</FilterChip>
              <FilterChip active={filterDuration === 'ending'} onClick={() => setFilterDuration('ending')} isDark={isDark}>Ultima settimana</FilterChip>
              <FilterChip active={filterDuration === 'month'} onClick={() => setFilterDuration('month')} isDark={isDark}>&lt; 1 mese</FilterChip>
              <FilterChip active={filterDuration === 'long'} onClick={() => setFilterDuration('long')} isDark={isDark}>&gt; 1 mese</FilterChip>
            </FilterSection>

            <FilterSection label="Stato" isDark={isDark}>
              <FilterChip active={filterStatus === 'all'} onClick={() => setFilterStatus('all')} isDark={isDark}>Tutte</FilterChip>
              <FilterChip active={filterStatus === 'new'} onClick={() => setFilterStatus('new')} isDark={isDark}>Nuove (7g)</FilterChip>
              <FilterChip active={filterStatus === 'upcoming'} onClick={() => setFilterStatus('upcoming')} isDark={isDark}>In partenza</FilterChip>
              <FilterChip active={filterStatus === 'justStarted'} onClick={() => setFilterStatus('justStarted')} isDark={isDark}>Appena iniziate</FilterChip>
            </FilterSection>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={resetFilters}
                className="btn-secondary flex-1"
                data-testid="public-challenges-filter-reset-btn"
              >
                Azzera
              </button>
              <button 
                onClick={() => setShowFilters(false)}
                className="flex-1 py-3 px-4 rounded-full bg-[#E8334A] text-white font-semibold"
                data-testid="public-challenges-filter-apply-btn"
              >
                Applica ({filteredPublic.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Modal */}
      {showJoinModal && (
        <JoinChallengeModal 
          isDark={isDark}
          onClose={() => setShowJoinModal(false)}
          onJoin={async (code) => {
            await joinChallenge(code);
            setShowJoinModal(false);
            loadChallenges();
          }}
          t={t}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateChallengeModal 
          isDark={isDark}
          onClose={() => setShowCreateModal(false)}
          onCreate={async (data) => {
            await createChallenge(data);
            setShowCreateModal(false);
            loadChallenges();
          }}
          t={t}
        />
      )}
    </div>
  );
}

function FilterSection({ label, isDark, children }) {
  return (
    <div className="mb-5">
      <p className={`text-xs font-medium mb-2 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {children}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, isDark, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        active
          ? 'bg-[#E8334A] text-white'
          : isDark
            ? 'bg-white/5 text-white/70 hover:bg-white/10'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

function ChallengeCard({ challenge, isDark, onClick, past, t }) {
  const daysLeft = Math.ceil((new Date(challenge.end_date) - new Date()) / (1000 * 60 * 60 * 24));
  
  return (
    <div 
      onClick={onClick}
      className={`glass-card p-4 cursor-pointer active:scale-[0.98] transition-transform ${past ? 'opacity-60' : ''}`}
      data-testid={`challenge-${challenge.challenge_id}`}
    >
      <div className="flex items-start gap-3">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
          style={{ backgroundColor: `${challenge.color}20` }}
        >
          {challenge.emoji}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-display truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {challenge.name}
            </h3>
            {challenge.is_public && (
              <Globe className="w-3.5 h-3.5 text-[#3B82F6] flex-shrink-0" />
            )}
          </div>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            <Users className="w-3 h-3 inline mr-1" />
            {challenge.participants?.length || 1} {t('challenges.participants')}
          </p>
        </div>
        
        <div className="text-right">
          {!past && (
            <span className={`text-sm font-medium ${
              daysLeft <= 3 ? 'text-[#E8334A]' : isDark ? 'text-white/60' : 'text-gray-500'
            }`}>
              {daysLeft > 0 ? `${daysLeft}${t('challenges.daysShort')}` : t('challenges.lastDay')}
            </span>
          )}
          <ChevronRight className={`w-5 h-5 mt-1 ml-auto ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
        </div>
      </div>
    </div>
  );
}

function PublicChallengeCard({ challenge, isDark, onJoin, onClick, t }) {
  const [joining, setJoining] = useState(false);
  const daysLeft = Math.ceil((new Date(challenge.end_date) - new Date()) / (1000 * 60 * 60 * 24));
  const participantCount = challenge.participant_count || challenge.participants?.length || 0;
  const maxParticipants = challenge.max_participants;
  
  const handleJoin = async (e) => {
    e.stopPropagation();
    setJoining(true);
    try {
      await onJoin();
    } finally {
      setJoining(false);
    }
  };
  
  return (
    <div 
      onClick={onClick}
      className={`glass-card p-4 cursor-pointer active:scale-[0.98] transition-transform ${isDark ? '' : ''}`}
      data-testid={`public-challenge-${challenge.challenge_id}`}
    >
      <div className="flex items-start gap-3">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
          style={{ backgroundColor: `${challenge.color}20` }}
        >
          {challenge.emoji}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className={`font-display ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {challenge.name}
          </h3>
          {challenge.description && (
            <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {challenge.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              <Users className="w-3 h-3 inline mr-1" />
              {participantCount}{maxParticipants ? `/${maxParticipants}` : ''}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              <Calendar className="w-3 h-3 inline mr-1" />
              {daysLeft}{t('challenges.daysShort')} {t('challenges.remaining')}
            </span>
          </div>
          <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            {t('challenges.by')} {challenge.creator_name}
          </p>
        </div>
        
        <button
          onClick={handleJoin}
          disabled={joining}
          className="px-4 py-2 rounded-full text-sm font-semibold bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-all disabled:opacity-50 flex-shrink-0"
        >
          {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : t('challenges.join')}
        </button>
      </div>
    </div>
  );
}

function JoinChallengeModal({ isDark, onClose, onJoin, t }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleJoin = async () => {
    if (!code.trim()) {
      setError(t('errors.validation'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onJoin(code.toUpperCase().trim());
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.validation'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[300] flex flex-col animate-slide-up"
      style={{ 
        background: isDark ? '#080D1A' : '#F8FAFC',
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}
    >
      {/* Fixed Header with back arrow */}
      <div className={`flex items-center px-4 py-3 border-b ${isDark ? 'border-white/10 bg-[#080D1A]' : 'border-gray-200 bg-white'}`}>
        <button 
          onClick={onClose}
          className={`p-2 -ml-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <ChevronRight className={`w-6 h-6 rotate-180 ${isDark ? 'text-white' : 'text-gray-900'}`} />
        </button>
        <h2 className={`flex-1 font-display text-lg text-center mr-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('challenges.joinModal.title')}
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-6">
        <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          {t('challenges.inviteCode')}
        </p>

        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder={t('challenges.joinModal.placeholder')}
          className="input-glass text-center text-xl tracking-widest font-mono mb-4"
          maxLength={8}
          data-testid="join-code-input"
        />
        
        {error && <p className="text-[#E8334A] text-sm text-center mb-4">{error}</p>}
      </div>

      {/* Fixed Bottom Buttons */}
      <div 
        className={`px-4 py-4 border-t ${isDark ? 'border-white/10 bg-[#080D1A]' : 'border-gray-200 bg-white'}`}
        style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">{t('common.cancel')}</button>
          <button 
            onClick={handleJoin}
            disabled={loading}
            className="btn-primary flex-1"
          >
            {loading ? t('challenges.joinModal.joining') : t('challenges.joinModal.join')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateChallengeModal({ isDark, onClose, onCreate, t }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    emoji: '🎯',
    color: '#E8334A',
    duration_weeks: 4,
    is_public: false,
    max_participants: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ name: '', description: '' });

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const emojis = ['🎯', '💪', '🔥', '⚡', '🏆', '🌟', '💎', '🚀'];
  const colors = ['#E8334A', '#16A34A', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899'];

  const handleCreate = async () => {
    // Reset field errors
    const errors = { name: '', description: '' };
    let hasError = false;

    if (!formData.name.trim()) {
      errors.name = t('errors.required') || 'Campo obbligatorio';
      hasError = true;
    }

    if (!formData.description.trim()) {
      errors.description = t('errors.required') || 'Campo obbligatorio';
      hasError = true;
    }

    setFieldErrors(errors);

    if (hasError) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + formData.duration_weeks * 7);

      await onCreate({
        name: formData.name,
        description: formData.description,
        emoji: formData.emoji,
        color: formData.color,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        is_public: formData.is_public,
        max_participants: formData.max_participants
      });
    } catch (err) {
      setError('Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[300] flex flex-col animate-slide-up"
      style={{ 
        background: isDark ? '#080D1A' : '#F8FAFC',
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}
    >
      {/* Fixed Header with back arrow */}
      <div className={`flex items-center px-4 py-3 border-b ${isDark ? 'border-white/10 bg-[#080D1A]' : 'border-gray-200 bg-white'}`}>
        <button 
          onClick={onClose}
          className={`p-2 -ml-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <ChevronRight className={`w-6 h-6 rotate-180 ${isDark ? 'text-white' : 'text-gray-900'}`} />
        </button>
        <h2 className={`flex-1 font-display text-lg text-center mr-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('challenges.createNew.title')}
        </h2>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="space-y-4 mb-6">
          {/* Public/Private Toggle */}
          <div>
            <label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('challenges.createNew.visibility')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setFormData({...formData, is_public: false, max_participants: null})}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                  !formData.is_public
                    ? 'bg-[#E8334A] text-white'
                    : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Lock className="w-4 h-4" />
                {t('challenges.private')}
              </button>
              <button
                onClick={() => setFormData({...formData, is_public: true})}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                  formData.is_public
                    ? 'bg-[#3B82F6] text-white'
                    : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Globe className="w-4 h-4" />
                {t('challenges.public')}
              </button>
            </div>
            <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {formData.is_public 
                ? t('challenges.createNew.visibility') 
                : t('challenges.inviteCode')}
            </p>
          </div>

          {/* Max Participants (only for public) */}
          {formData.is_public && (
            <div>
              <label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('challenges.createNew.maxParticipants')} ({t('common.optional')})
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[null, 10, 25, 50, 100].map(max => (
                  <button
                    key={max || 'unlimited'}
                    onClick={() => setFormData({...formData, max_participants: max})}
                    className={`py-2 rounded-xl text-sm font-medium transition-all ${
                      formData.max_participants === max
                        ? 'bg-[#3B82F6] text-white'
                        : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {max || '∞'}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('challenges.createNew.name')}</label>
              {fieldErrors.name && <span className="text-xs text-[#E8334A]">{fieldErrors.name}</span>}
            </div>
            <input
              type="text"
              value={formData.name}
              onChange={e => {
                setFormData({...formData, name: e.target.value});
                if (fieldErrors.name) setFieldErrors({...fieldErrors, name: ''});
              }}
              placeholder={t('challenges.createNew.name')}
              className={`input-glass ${fieldErrors.name ? 'border-[#E8334A] border-2' : ''}`}
              data-testid="challenge-name-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('challenges.createNew.description')}
              </label>
              {fieldErrors.description && <span className="text-xs text-[#E8334A]">{fieldErrors.description}</span>}
            </div>
            <textarea
              value={formData.description}
              onChange={e => {
                setFormData({...formData, description: e.target.value});
                if (fieldErrors.description) setFieldErrors({...fieldErrors, description: ''});
              }}
              placeholder={t('challenges.createNew.description')}
              rows={2}
              className={`input-glass resize-none ${fieldErrors.description ? 'border-[#E8334A] border-2' : ''}`}
            />
          </div>

          <div>
            <label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Emoji</label>
            <div className="flex gap-2 flex-wrap">
              {emojis.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => setFormData({...formData, emoji})}
                  className={`w-12 h-12 rounded-xl text-2xl flex items-center justify-center transition-all ${
                    formData.emoji === emoji
                      ? 'bg-[#E8334A] ring-4 ring-[#E8334A]/30 scale-110 shadow-lg'
                      : isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('challenges.createNew.color')}</label>
            <div className="flex gap-2">
              {colors.map(color => (
                <button
                  key={color}
                  onClick={() => setFormData({...formData, color})}
                  className={`w-12 h-12 rounded-xl transition-all ${
                    formData.color === color 
                      ? 'ring-4 ring-white scale-110 shadow-lg' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: color }}
                >
                  {formData.color === color && (
                    <svg className="w-6 h-6 mx-auto text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('challenges.createNew.duration')}</label>
            <div className="grid grid-cols-4 gap-2">
              {[2, 4, 8, 12].map(weeks => (
                <button
                  key={weeks}
                  onClick={() => setFormData({...formData, duration_weeks: weeks})}
                  className={`p-3 rounded-xl text-center transition-all ${
                    formData.duration_weeks === weeks
                      ? 'bg-[#E8334A] text-white'
                      : isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="font-display font-bold">{weeks}</span>
                  <span className="text-xs block opacity-70">{t('challenges.createNew.weeks')}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="text-[#E8334A] text-sm text-center mb-4">{error}</p>}
      </div>

      {/* Fixed Bottom Buttons */}
      <div 
        className={`px-4 py-4 border-t ${isDark ? 'border-white/10 bg-[#080D1A]' : 'border-gray-200 bg-white'}`}
        style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">{t('common.cancel')}</button>
          <button 
            onClick={handleCreate}
            disabled={loading}
            className="btn-primary flex-1"
            data-testid="confirm-create-challenge"
          >
            {loading ? t('challenges.createNew.creating') : t('challenges.createNew.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
